import { ProgramToken } from 'types';
import { BigNumber, ethers } from 'ethers';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { parseUnits } from 'ethers/lib/utils';
import { useWeb3React } from 'hooks/web3';
import IUniswapV2ERC20 from '@uniswap/v2-core/build/IUniswapV2ERC20.json';

export const usePoolBalance = (fieldkey: string, poolAddress: string, token?: ProgramToken) => {
  const { watch, setError } = useFormContext();
  const amount = watch(fieldkey);

  const { provider } = useWeb3React();
  const [balance, setBalance] = useState(BigNumber.from(0));
  const [decimals, setDecimals] = useState(0);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [zeroAmount, setZeroAmount] = useState(Number(amount) === 0);
  const [nullAmount, setNullAmount] = useState(!amount);

  const reset = useCallback(() => {
    setBalance(BigNumber.from(0));
    setDecimals(0);
    setInsufficientBalance(false);
    setZeroAmount(false);
    setNullAmount(true);
  }, []);

  const validateAmount = useCallback(
    (poolBalance: BigNumber) => {
      if (!amount) {
        setNullAmount(true);
        return;
      } else {
        setNullAmount(false);
      }

      if (Number(amount) === 0) {
        setZeroAmount(true);
      } else {
        setZeroAmount(false);
      }

      if (BigNumber.from(poolBalance).gte(0)) {
        const insufficientBal = poolBalance.lt(parseUnits(amount, decimals));
        if (insufficientBal) {
          setInsufficientBalance(true);
          setError(fieldkey, { message: 'Insufficient pool balance' });
        } else {
          setInsufficientBalance(false);
        }
      }
    },
    [amount, decimals, fieldkey, setError]
  );

  const getPoolBalance = useCallback(
    async (token: ProgramToken) => {
      try {
        const tokenContract = new ethers.Contract(
          token.address,
          IUniswapV2ERC20.abi,
          provider
        );

        const tokenDecimalsPromise = tokenContract.decimals();
        const poolBalancePromise = tokenContract.balanceOf(poolAddress);

        const [tokenDecimals, poolBalance] = await Promise.all([
          tokenDecimalsPromise,
          poolBalancePromise
        ]);

        setDecimals(tokenDecimals);
        setBalance(poolBalance);

        validateAmount(poolBalance);
      } catch (e) {
        reset();
        console.log(e);
      }
    },
    [provider, reset, validateAmount, poolAddress]
  );

  useEffect(() => {
    if (!token) {
      reset();
      return;
    } else {
      getPoolBalance(token);
    }
  }, [token, amount, reset, getPoolBalance]);

  return {
    balance,
    decimals,
    insufficientBalance,
    amount,
    zeroAmount,
    nullAmount
  };
};
